import React from "react";
import { Container, Row, Col, Card } from "reactstrap";
import { Building2, HardHat, Navigation, Map, CheckCircle2 } from "lucide-react";
import {
  NA_PRIMARY,
  NA_SECONDARY,
  naReadableText,
  naGradientLine,
  naDarkCardBase,
  naCardBgHover,
  onDarkCardLeave,
} from "./naSectionTokens";

const FOCUS_AREAS = [
  {
    icon: Building2,
    title: "Smart cities & urban digital twins",
    color: NA_PRIMARY,
    bullets: [
      "City-scale digital twin platforms",
      "Real-time urban operations",
      "AI-driven smart city management",
      "Climate resilience & disaster response",
      "GIS integration & spatial analytics",
      "Connected urban ecosystems",
    ],
  },
  {
    icon: HardHat,
    title: "Construction & digital engineering",
    color: NA_SECONDARY,
    bullets: [
      "BIM-to-digital twin workflows",
      "Digital project delivery",
      "Smart construction & connected job sites",
      "Lifecycle asset management",
      "AI-enabled project analytics",
      "Sustainable infrastructure delivery",
    ],
  },
  {
    icon: Navigation,
    title: "Transportation & mobility",
    color: NA_PRIMARY,
    bullets: [
      "Airport digital twins",
      "Intelligent transportation systems",
      "Rail & transit modernization",
      "Smart corridor planning",
      "Traffic simulation & mobility optimization",
      "Predictive infrastructure maintenance",
    ],
  },
  {
    icon: Map,
    title: "Urban planning & infrastructure",
    color: NA_SECONDARY,
    bullets: [
      "Data-driven urban planning",
      "Regional infrastructure modeling",
      "Utilities & energy systems",
      "Water infrastructure digital twins",
      "Cross-agency collaboration",
      "Future-ready infrastructure strategies",
    ],
  },
];

function FocusCard({ item, size }) {
  const Icon = item.icon;
  const isPrimary = item.color === NA_PRIMARY;

  return (
    <Col lg={6} className="mb-4">
      <Card
        style={{
          ...naDarkCardBase,
          borderRadius: "20px",
          padding: 0,
          overflow: "hidden",
          height: "100%",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          cursor: "pointer",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = naCardBgHover;
          e.currentTarget.style.borderColor = isPrimary
            ? "rgba(198, 96, 103, 0.45)"
            : "rgba(112, 174, 184, 0.45)";
          e.currentTarget.style.transform = "translateY(-6px)";
          e.currentTarget.style.boxShadow = isPrimary
            ? "0 24px 48px rgba(198, 96, 103, 0.2)"
            : "0 24px 48px rgba(112, 174, 184, 0.2)";
          const iconEl = e.currentTarget.querySelector(".na-focus-icon-wrap");
          if (iconEl) {
            iconEl.style.transform = "scale(1.08) rotate(-3deg)";
          }
        }}
        onMouseLeave={(e) => {
          onDarkCardLeave(e);
          const iconEl = e.currentTarget.querySelector(".na-focus-icon-wrap");
          if (iconEl) {
            iconEl.style.transform = "scale(1) rotate(0deg)";
          }
        }}
      >
        <div style={{ padding: size ? "1.75rem" : "1.35rem" }}>
          <div
            className="na-focus-icon-wrap d-flex align-items-center justify-content-center mb-3"
            style={{
              width: size ? 64 : 56,
              height: size ? 64 : 56,
              borderRadius: "18px",
              background: `linear-gradient(145deg, ${item.color}33, ${item.color}14)`,
              border: `2px solid ${item.color}55`,
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Icon size={size ? 32 : 28} strokeWidth={2} style={{ color: item.color }} />
          </div>
          <h3
            className="text-white mb-3"
            style={{
              fontSize: size ? "1.12rem" : "1rem",
              fontWeight: 700,
              letterSpacing: "0.03em",
              textTransform: "uppercase",
              lineHeight: 1.35,
              ...naReadableText,
            }}
          >
            {item.title}
          </h3>
          <ul className="list-unstyled mb-0">
            {item.bullets.map((b, i) => (
              <li key={i} className="d-flex mb-2" style={{ ...naReadableText }}>
                <CheckCircle2
                  className="flex-shrink-0 mt-1"
                  size={16}
                  style={{ color: item.color, marginRight: "0.55rem" }}
                  strokeWidth={2.2}
                />
                <span style={{ fontSize: size ? "0.95rem" : "0.88rem", lineHeight: 1.55 }}>{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div
          style={{
            height: "4px",
            width: "100%",
            background: `linear-gradient(90deg, ${item.color}, transparent)`,
            opacity: 0.75,
          }}
        />
      </Card>
    </Col>
  );
}

function NAFocusAreasSection() {
  const size =
    document.body.clientWidth >= 928 || document.documentElement.clientWidth >= 928;
  const heroImg = require("assets/titleimages/2.jpg");

  return (
    <section className="section py-5" style={{ background: "transparent", position: "relative", zIndex: 1 }}>
      <Container>
        <Row className="justify-content-center mb-4">
          <Col md={10} lg={9} className="text-center">
            <h2
              className="mb-3"
              style={{
                fontSize: size ? "2.25rem" : "1.75rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
                lineHeight: 1.2,
                textTransform: "uppercase",
                color: NA_PRIMARY,
                ...naReadableText,
              }}
            >
              Event focus areas
            </h2>
            <div
              style={{
                width: "100px",
                height: "4px",
                borderRadius: "2px",
                margin: size ? "0 auto 2rem" : "0 auto 1.25rem",
                ...naGradientLine,
              }}
            />
          </Col>
        </Row>
        <Row className="justify-content-center mb-5">
          <Col xs={12}>
            <img
              src={heroImg}
              alt="Conference focus"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "300px",
                objectFit: "cover",
                borderRadius: "16px",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
              }}
            />
          </Col>
        </Row>
        <Row>
          {FOCUS_AREAS.map((item, i) => (
            <FocusCard key={i} item={item} size={size} />
          ))}
        </Row>
      </Container>
    </section>
  );
}

export default NAFocusAreasSection;
